import type CSS from 'csstype'
import { useMemo, useRef } from 'react'

import { useUnitTranslateFunction } from 'aem-libs-react-ts/src/hooks/useUnitTranslate'
import { ExcludeFromListType, Maybe } from 'aem-libs-ts/src/types/utility'
import { conditionalInvoke } from 'aem-libs-ts/src/utils/common/conditionalInvoke'
import { isDefined } from 'aem-libs-ts/src/utils/common/isDefined'
import { pipe } from 'aem-libs-ts/src/utils/common/pipe'
import { tuple } from 'aem-libs-ts/src/utils/common/tuple'
import { numberFormat } from 'aem-libs-ts/src/utils/formatting/numbers/numberFormat'

import { NumberInputRef } from '../number-field'
import { PlusMinusNumberField, PlusMinusNumberFieldProps } from '../plus-minus-number-field'
import { RangeType, Slider } from '../slider'
import * as PlusMinusSliderFieldStyled from './PlusMinusSliderField.styled'
import { SliderContainer } from './PlusMinusSliderField.styled'


type FlexDirection = CSS.Property.FlexDirection
export type ChangeSource = 'slider' | 'input'

export interface PlusMinusSliderFieldProps extends Omit<PlusMinusNumberFieldProps, 'onChangeComplete'> {
  inputDirection?: FlexDirection
  range?: RangeType[]
  rangeStep?: number
  hideButtons?: boolean
  onChangeComplete: (value: number, changeSource: ChangeSource) => void
  onSliderChange?: (value: number) => void
  onInputChange?: (value: number) => void
}

export const PlusMinusSliderField = ({
  range,
  rangeStep,
  hideButtons,
  inputDirection,
  onInputChange,
  onSliderChange,
  testId,
  ...plusMinusProps
}: PlusMinusSliderFieldProps) => {
  const { getUnitTranslate } = useUnitTranslateFunction()
  const inputRef = useRef<NumberInputRef | null>(null)

  const validateSliderConfig = (
    configList: readonly [number | undefined, number | undefined, number | undefined]
  ): ExcludeFromListType<typeof configList, undefined> => {
    const isValid = configList.filter(isDefined).length === configList.length

    if (isValid) {
      return configList as ExcludeFromListType<typeof configList, undefined>
    }

    throw new Error('Slider has invalid props.')
  }

  const sliderRange = useMemo(() => {
    if (range && range.length > 0) {
      return range
    }

    return pipe(() => tuple(plusMinusProps.min, plusMinusProps.max, plusMinusProps.step))
      .try(validateSliderConfig)
      .map(([min, max, step]) => [{ value: min, step }, { value: max }])
      .getOrElse(() => {
        throw Error('Slider has invalid props.')
      })
  }, [plusMinusProps.min, plusMinusProps.max, plusMinusProps.step, range, rangeStep])

  const processValueChange = (changedValue: number) => {
    const newValue = (isDefined(changedValue) && Math.round(changedValue)) || changedValue
    conditionalInvoke(plusMinusProps.onChange, [newValue])
    return newValue
  }

  const handleSliderChange = (sliderValue: number) => {
    const newValue = processValueChange(sliderValue)
    inputRef.current?.setValue(newValue)

    conditionalInvoke(onSliderChange, [newValue])
  }

  const handleInputChange = (inputValue: number) => {
    const newValue = processValueChange(inputValue)
    conditionalInvoke(onInputChange, [newValue])
  }

  const handleChangeComplete = (sliderValue: number, changeSource: ChangeSource) => {
    const newValue = (isDefined(sliderValue) && Math.round(sliderValue)) || sliderValue
    plusMinusProps.onChangeComplete(newValue, changeSource)
  }

  const getSliderLabel = (value: Maybe<number>) => {
    return isDefined(value)
      ? `${numberFormat(value as number)} ${getUnitTranslate(
          value as number,
          plusMinusProps.unitType,
          plusMinusProps.unitCustom
        )}`
      : null
  }

  return (
    <PlusMinusSliderFieldStyled.Container inputDirection={inputDirection} hideButtons={hideButtons}>
      <SliderContainer>
        <Slider
          minLabel={getSliderLabel(plusMinusProps.min)}
          maxLabel={getSliderLabel(plusMinusProps.max)}
          value={plusMinusProps.value}
          range={sliderRange}
          onChange={handleSliderChange}
          onChangeComplete={(value) => handleChangeComplete(value, 'slider')}
          disabled={plusMinusProps.disabled}
          enableGaTracking
          id={plusMinusProps.id}
          testId={testId}
          trackElement={plusMinusProps.trackingDescription}
        />
      </SliderContainer>
      <PlusMinusNumberField
        {...plusMinusProps}
        ref={inputRef}
        value={plusMinusProps.value}
        onChange={handleInputChange}
        onChangeComplete={(value) => handleChangeComplete(value, 'input')}
        hideValidation
        testId={testId}
      />
    </PlusMinusSliderFieldStyled.Container>
  )
}
